import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  DropzoneOptions,
  ErrorCode,
  FileRejection,
  FileWithPath,
  useDropzone,
} from 'react-dropzone';
import invariant from 'ts-invariant';
import DropArea from './DropArea';
import cn from '../../utils/cn';
import FilePreviewPrimitive, {
  FilePreviewVariant,
} from '../../atoms/file-preview/FilePreview';

export enum DropZoneVariant {
  DOCUMENT = 'DOCUMENT',
  MEDIA = 'MEDIA',
}

const DEFAULT_MAX_SIZE_MB: number = 12;

const FILE_TYPES: Record<string, Record<string, string[]>> = {
  document: {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
      '.pptx',
    ],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
  },
  image: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
  },
};

const isDropZoneErrorCode = (value: string): value is ErrorCode => {
  return Object.values(ErrorCode).includes(value as ErrorCode);
};

type DropZoneContextBase = {
  variant: DropZoneVariant;
  maxSizeMB: number;
  maxFiles?: number;
  errorCode?: ErrorCode;
  dragEnter: boolean;
  clearError: () => void;
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
};

type SingleFileContext = DropZoneContextBase & {
  multiple: false;
  value: FileWithPath | null;
  removeFile: () => void;
};

type MultipleFilesContext = DropZoneContextBase & {
  multiple: true;
  value: FileWithPath[];
  removeFile: (file: FileWithPath) => void;
};

type DropZoneContextType = SingleFileContext | MultipleFilesContext;

const DropZoneContext = createContext<DropZoneContextType | undefined>(undefined);

export const useDropZone = (): DropZoneContextType => {
  const context = useContext(DropZoneContext);

  invariant(context, 'useDropZone must be used within a DropZone component');

  return context;
};

type RootPropsBase = {
  variant?: DropZoneVariant;
  maxSizeMB?: number;
} & Omit<DropzoneOptions, 'multiple'> &
  React.ComponentPropsWithoutRef<'div'>;

type SingleFileProps = RootPropsBase & {
  multiple?: false;
  value: FileWithPath | null;
  onValueChange: (value: FileWithPath | null) => void;
};

type MultipleFilesProps = RootPropsBase & {
  multiple: true;
  value: FileWithPath[];
  onValueChange: (value: FileWithPath[]) => void;
  maxFiles?: number;
};

type RootProps = SingleFileProps | MultipleFilesProps;

const Root = memo((props: RootProps) => {
  const {
    variant = DropZoneVariant.DOCUMENT,
    maxSizeMB = DEFAULT_MAX_SIZE_MB,
    multiple = false,
    value,
    onValueChange,
    className,
    children,
    ...dropzoneOptions
  } = props;

  const [errorCode, setErrorCode] = useState<ErrorCode | undefined>(undefined);
  const [dragEnter, setDragEnter] = useState<boolean>(false);

  const maxFiles = props.multiple ? props.maxFiles : 1;
  const maxSize = useMemo(() => maxSizeMB * 10 ** 6, [maxSizeMB]);

  const accept = useMemo(
    () =>
      variant === DropZoneVariant.DOCUMENT
        ? { ...FILE_TYPES.document, ...FILE_TYPES.image }
        : FILE_TYPES.image,
    [variant],
  );

  const handleDrop = useCallback(
    (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      setDragEnter(false);
      setErrorCode(undefined);

      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const error = rejection.errors[0];
        const code = isDropZoneErrorCode(error.code) ? error.code : undefined;
        setErrorCode(code);

        return;
      }

      if (acceptedFiles.length === 0) {
        return;
      }

      if (multiple) {
        (onValueChange as (value: FileWithPath[]) => void)([
          ...(value as FileWithPath[]),
          ...acceptedFiles,
        ]);

        return;
      }

      (onValueChange as (value: FileWithPath | null) => void)(acceptedFiles[0]);
    },
    [multiple, onValueChange, value],
  );

  const handleDragEnter = useCallback(() => {
    setErrorCode(undefined);
    setDragEnter(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setErrorCode(undefined);
    setDragEnter(false);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    maxSize,
    accept,
    multiple,
    maxFiles,
    onDrop: handleDrop,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onFileDialogOpen: handleDragEnter,
    onFileDialogCancel: handleDragLeave,
    ...dropzoneOptions,
  });

  const clearError = useCallback(() => setErrorCode(undefined), []);

  const contextValue = useMemo((): DropZoneContextType => {
    const base = {
      variant,
      maxSizeMB,
      errorCode,
      dragEnter,
      clearError,
      getRootProps,
      getInputProps,
    };

    if (multiple) {
      return {
        ...base,
        multiple: true,
        maxFiles,
        value: value as FileWithPath[],
        removeFile: (file: FileWithPath) => {
          (onValueChange as (value: FileWithPath[]) => void)(
            (value as FileWithPath[]).filter((f) => f !== file),
          );
        },
      };
    }

    return {
      ...base,
      multiple: false,
      value: value as FileWithPath | null,
      removeFile: () => {
        (onValueChange as (value: FileWithPath | null) => void)(null);
      },
    };
  }, [
    variant,
    maxSizeMB,
    errorCode,
    dragEnter,
    clearError,
    getRootProps,
    getInputProps,
    multiple,
    value,
    maxFiles,
    onValueChange,
  ]);

  return (
    <DropZoneContext.Provider value={contextValue}>
      <div className={className}>{children}</div>
    </DropZoneContext.Provider>
  );
});

Root.displayName = 'DropZone.Root';

type FilePreviewProps = {
  file: FileWithPath;
} & Omit<
  React.ComponentPropsWithoutRef<typeof FilePreviewPrimitive>,
  'variant' | 'onRemove' | 'fileName' | 'fileSrc' | 'fileType'
>;

const FilePreview = memo(({ file, ...props }: FilePreviewProps) => {
  const dropZone = useDropZone();

  const handleRemove = useCallback(() => {
    if (dropZone.multiple) {
      dropZone.removeFile(file);
    } else {
      dropZone.removeFile();
    }
  }, [dropZone, file]);

  const variant =
    dropZone.variant === DropZoneVariant.MEDIA
      ? FilePreviewVariant.MEDIA
      : FilePreviewVariant.DOCUMENT;

  const fileSrc = useMemo(() => URL.createObjectURL(file), [file]);

  if (variant === FilePreviewVariant.MEDIA) {
    return (
      <FilePreviewPrimitive
        variant={FilePreviewVariant.MEDIA}
        fileName={file.name}
        fileSrc={fileSrc}
        onRemove={handleRemove}
        {...props}
      />
    );
  }

  return (
    <FilePreviewPrimitive
      variant={FilePreviewVariant.DOCUMENT}
      mimeType={file.type}
      fileName={file.name}
      fileSrc={fileSrc}
      onRemove={handleRemove}
      {...props}
    />
  );
});

FilePreview.displayName = 'DropZone.FilePreview';

const FilePreviewCompound = Object.assign(FilePreview, {
  Root: FilePreviewPrimitive.Root,
  Icon: FilePreviewPrimitive.Icon,
  Message: FilePreviewPrimitive.Message,
  Image: FilePreviewPrimitive.Image,
  Actions: FilePreviewPrimitive.Actions,
});

const DropZone = memo(({ variant, value, ...props }: SingleFileProps) => {
  return (
    <Root multiple={false} variant={variant} value={value} {...props}>
      {value === null ? (
        <DropArea
          className={cn({
            'size-full': variant === DropZoneVariant.MEDIA,
          })}
        />
      ) : (
        <FilePreview file={value} />
      )}
    </Root>
  );
});

DropZone.displayName = 'DropZone';

const DropZoneCompound = Object.assign(DropZone, {
  Root,
  DropArea,
  FilePreview: FilePreviewCompound,
});

export default DropZoneCompound;
